import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * MiniChart Component - Displays a time series chart with live data
 * @param {Object} props
 * @param {Array} props.data - Array of {timestamp, value} objects
 * @param {string} props.unit - Unit of measurement for the values
 * @param {Object} props.sessionBounds - Session min/max bounds {min, max}
 */
function MiniChart({ data, unit, sessionBounds, darkMode }) {
  if (!data || data.length === 0) {
    return <div className="no-data">Waiting for real-time data...</div>;
  }

  const values = data.map((d) => d.value);

  if (values.length === 0) {
    return <div className="no-data">No data points available</div>;
  }

  // Use session bounds if available, otherwise calculate from current data
  const minValue = sessionBounds?.min ?? Math.min(...values);
  const maxValue = sessionBounds?.max ?? Math.max(...values);

  // Calculate mean for stable scaling centered around average
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;

  // Calculate max deviation from mean
  const maxDeviation = Math.max(
    Math.abs(maxValue - mean),
    Math.abs(minValue - mean)
  );

  // Use mean-based symmetric scale for stability
  // Add padding for visual comfort
  const paddingMultiplier = 3;
  let domainMin = mean - maxDeviation * paddingMultiplier;
  let domainMax = mean + maxDeviation * paddingMultiplier;

  // Handle edge case where all values are the same
  if (maxDeviation === 0 || !isFinite(maxDeviation)) {
    domainMin = mean - (Math.abs(mean) * 0.1 || 1);
    domainMax = mean + (Math.abs(mean) * 0.1 || 1);
  }

  // Calculate nice tick interval for equal distribution
  const domainRange = domainMax - domainMin;
  const roughTickInterval = domainRange / 5; // Aim for ~5 ticks
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughTickInterval)));
  const normalized = roughTickInterval / magnitude;

  // Round to nice numbers (1, 2, 5)
  let niceInterval;
  if (normalized <= 1) niceInterval = magnitude;
  else if (normalized <= 2) niceInterval = 2 * magnitude;
  else if (normalized <= 5) niceInterval = 5 * magnitude;
  else niceInterval = 10 * magnitude;

  // Adjust domain to align with nice intervals
  const niceDomainMin = Math.floor(domainMin / niceInterval) * niceInterval;
  const niceDomainMax = Math.ceil(domainMax / niceInterval) * niceInterval;

  // Generate tick values for equal distribution
  const ticks = [];
  for (let tick = niceDomainMin; tick <= niceDomainMax; tick += niceInterval) {
    ticks.push(Number(tick.toFixed(10))); // Avoid floating point errors
  }

  // Format data for Recharts - add index for x-axis
  const chartData = data.map((point, index) => ({
    index: index,
    value: point.value,
    timestamp: point.timestamp,
  }));

  // Custom tooltip to show timestamp and value
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const tooltipData = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            padding: "8px 12px",
            borderRadius: "4px",
            color: "white",
            fontSize: "12px",
          }}
        >
          <p style={{ margin: 0 }}>
            {tooltipData.value.toFixed(2)} {unit}
          </p>
          <p style={{ margin: "4px 0 0 0", fontSize: "10px", opacity: 0.8 }}>
            {new Date(tooltipData.timestamp).toLocaleTimeString()}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart">
      <ResponsiveContainer width="100%" height={200}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 10, left: -5, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={darkMode ? "#3a3a3a" : "#e0e0e0"}
            horizontal={true}
            vertical={false}
          />
          <XAxis dataKey="index" hide={true} />
          <YAxis
            domain={[niceDomainMin, niceDomainMax]}
            ticks={ticks}
            hide={false}
            width={Math.abs(niceDomainMax) >= 100 || Math.abs(niceDomainMin) >= 100 ? 50 : 40}
            tick={{ fontSize: 9, fill: darkMode ? "#666" : "#999" }}
            tickFormatter={(value) => value.toFixed(2)}
            orientation="left"
            axisLine={false}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="value"
            stroke="#ff6b6b"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, fill: "#ff6b6b" }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="chart-labels">
        <span className="chart-label-min">
          Min: {minValue.toFixed(2)} {unit}
        </span>
        <span className="chart-label-max">
          Max: {maxValue.toFixed(2)} {unit}
        </span>
      </div>
    </div>
  );
}

export default MiniChart;
